import { useQuery } from 'react-query';
import API from '../API';
import ScheduleCacheService from '../ScheduleCacheService';

/**
 * Hook for fetching schedule data from API with caching
 */
export function useScheduleData(area, year, month, useTeams) {
  const queryKey = ['scheduleData', area, year, month, useTeams];

  return useQuery(
    queryKey,
    async () => {
      // Try to get from cache first
      const cached = ScheduleCacheService.get(area, year, month);
      if (cached) {
        return cached;
      }

      // If no cache, fetch from API
      const endpoint = useTeams
        ? `ContactSchedule/ListMonthTeam/${area}/${year}/${month}`
        : `ContactSchedule/ListMonthWeb/${area}/${year}/${month}`;

      const response = await API.sendRequest(endpoint, 'get');

      if (response.status === 200 && response.data) {
        // Save to cache
        ScheduleCacheService.set(area, year, month, response.data);
        return response.data;
      }

      throw new Error('Failed to fetch schedule data');
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error('[HOOK] Error fetching schedule data:', error);
      },
    }
  );
}

/**
 * Hook for fetching user availability
 */
export function useUserAvailability(year, month) {
  const yearMonth = `${year}-${String(month).padStart(2, '0')}`;
  const queryKey = ['userAvailability', yearMonth];

  return useQuery(
    queryKey,
    async () => {
      const response = await API.sendRequest(`UserAvailability/${yearMonth}`, 'get');

      if (response.status === 200 && response.data) {
        return response.data;
      }

      throw new Error('Failed to fetch user availability');
    },
    {
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error('[HOOK] Error fetching user availability:', error);
      }
    }
  );
}

/**
 * Hook for fetching holidays for a month
 */
export function useHolidays(year, month) {
  const queryKey = ['holidays', year, month];

  return useQuery(
    queryKey,
    async () => {
      const response = await API.sendRequest(
        `Holiday/ListByMonth/${year}/${month}`,
        'get'
      );

      if (response.status === 200 && response.data) {
        return response.data;
      }

      throw new Error('Failed to fetch holidays');
    },
    {
      staleTime: 24 * 60 * 60 * 1000, // 24 hours (holidays don't change)
      cacheTime: 7 * 24 * 60 * 60 * 1000, // 7 days
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 1000,
      onError: (error) => {
        console.error('[HOOK] Error fetching holidays:', error);
      }
    }
  );
}

/**
 * Hook for fetching Periods list (shared across areas)
 */
export function usePeriods() {
  return useQuery(
    ['periods'],
    async () => {
      const response = await API.sendRequest('Period', 'get');

      if (response.status === 200 && response.data) {
        return response.data;
      }

      throw new Error('Failed to fetch periods');
    },
    {
      staleTime: 60 * 60 * 1000, // 1 hour (rarely changes)
      cacheTime: 2 * 60 * 60 * 1000, // 2 hours
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error('[HOOK] Error fetching periods:', error);
      },
    }
  );
}

/**
 * Hook for fetching Appointment Types per area
 */
export function useAppointmentTypes(area) {
  const queryKey = ['appointmentTypes', area];

  return useQuery(
    queryKey,
    async () => {
      const response = await API.sendRequest(`AppointmentType/${area}`, 'get');

      if (response.status === 200 && response.data) {
        return response.data;
      }

      throw new Error('Failed to fetch appointment types');
    },
    {
      enabled: !!area,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error('[HOOK] Error fetching appointment types:', error);
      },
    }
  );
}

/**
 * Invalidate cache for a given month/area.
 *
 * By default this DOES NOT trigger an immediate refetch of the active query
 * (refetchActive=false). The localStorage cache is cleared and the
 * React Query cache is marked stale so the next mount / window-focus / month
 * change will fetch fresh data. This avoids freezing the grid right after a
 * user save (the optimistic update is the source of truth in that flow).
 *
 * Pass { refetchActive: true } when you really want to force a refetch right
 * now (e.g. after a destructive bulk operation where the optimistic state
 * cannot be trusted).
 *
 * @param {number} area - Area ID
 * @param {number} year - Year
 * @param {number} month - Month
 * @param {object} queryClient - React Query queryClient instance
 * @param {object} [options]
 * @param {boolean} [options.refetchActive=false] - Force immediate refetch of active queries
 */
export function invalidateScheduleCache(
  area,
  year,
  month,
  queryClient,
  options = {}
) {
  const { refetchActive = false, clearLocalStorage = false } = options;

  // 1. Clear localStorage ONLY if explicitly requested
  // By default we DON'T clear it, so that returning to a month uses cached data
  // (including optimistic updates) instead of fetching from slow API
  if (clearLocalStorage) {
    ScheduleCacheService.clear(area, year, month);
  }

  // 2. Invalidate React Query (marks data as stale, triggers refetch if refetchActive=true)
  queryClient.invalidateQueries(['scheduleData', area, year, month], {
    refetchActive,
  });
  queryClient.invalidateQueries(
    ['userAvailability', `${year}-${String(month).padStart(2, '0')}`],
    { refetchActive }
  );
}
